import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Divider,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Add, Delete, Save, FileDownload, RestartAlt, ArrowDropDown, Edit, Check } from '@mui/icons-material';
import { createQuote, updateQuote, exportQuote } from '../../services/api';
import { saveAs } from 'file-saver';
import { useAuth } from '../context/AuthContext';
import { PRIMARY, SECONDARY, CARD_SHADOW, FONT_HEADING, FONT_BODY, RADIUS } from '../theme';

const STATUS_OPTIONS = ['DRAFT', 'SENT', 'ACCEPTED', 'DECLINED'];

const EMPTY_LINE_ITEM = { qty: 1, product: '', description: '', unitPrice: 0, discount: 0 };

const DEFAULT_PAYMENT_TERMS = '50% deposit due upon order confirmation to initiate procurement/production. Remaining 50% balance due upon completion of on-site installation (or prior to shipment for equipment only orders).';
const DEFAULT_LATE_FEE_TERMS = 'Invoices not paid within terms are subject to a late charge of 1.5% per month (18% per annum) on the outstanding balance, or the maximum rate permitted by law.';
const DEFAULT_CONFIDENTIALITY_NOTICE = 'This quotation is CONFIDENTIAL and intended solely for the recipient named above; it may not be shared or disclosed to any third party without Tailored Alloys\' prior written consent. Prices are firm for 30 days from the date above. This quotation is subject to Tailored Alloys\' Terms and Conditions (see the "Terms and Conditions" tab), which are incorporated herein by reference.';

function blankForm() {
  return {
    id: null,
    quoteNumber: '',
    rfqId: null,
    customerName: '',
    customerId: '',
    customerTitle: '',
    customerCompany: '',
    customerAddress: '',
    customerEmail: '',
    customerPhone: '',
    shipToName: '',
    shipToAddress: '',
    salesPerson: '',
    poNumber: '',
    shipDate: '',
    shipVia: '',
    fobPoint: '',
    terms: '50% on order / 50% at install',
    lineItems: [{ ...EMPTY_LINE_ITEM }],
    salesTax: 0,
    otherAmount: 0,
    leadTime: '',
    contactPersonName: '',
    contactPersonPhone: '',
    contactPersonEmail: '',
    paymentTerms: DEFAULT_PAYMENT_TERMS,
    lateFeeTerms: DEFAULT_LATE_FEE_TERMS,
    confidentialityNotice: DEFAULT_CONFIDENTIALITY_NOTICE,
    status: 'DRAFT',
    quoteDate: new Date().toISOString().slice(0, 10),
  };
}

function toFormShape(source) {
  const base = blankForm();
  if (!source) return base;
  return {
    ...base,
    ...source,
    lineItems: Array.isArray(source.lineItems) && source.lineItems.length ? source.lineItems : base.lineItems,
    quoteDate: source.quoteDate ? String(source.quoteDate).slice(0, 10) : base.quoteDate,
  };
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.6rem',
    fontFamily: FONT_BODY,
    '&.Mui-focused fieldset': { borderColor: PRIMARY },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

const footerLabelSx = {
  display: 'block',
  fontFamily: FONT_HEADING,
  textTransform: 'uppercase',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: '#999',
  letterSpacing: 0.5,
  mb: 0.25,
};

function lineAmount(item) {
  // discount is signed: negative = discount, positive = additional charge
  return (Number(item.qty) || 0) * (Number(item.unitPrice) || 0) + (Number(item.discount) || 0);
}

/**
 * Editable quote form — used both standalone (Quotes page) and when generated from an RFQ.
 */
export default function QuoteForm({ initialQuote, onBack, onSaved }) {
  const { admin } = useAuth();
  const [form, setForm] = useState(() => toFormShape(initialQuote));
  const [savedSnapshot, setSavedSnapshot] = useState(() => toFormShape(initialQuote));
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [footerEditing, setFooterEditing] = useState(false);

  useEffect(() => {
    const shaped = toFormShape(initialQuote);
    // Not-yet-saved quotes (blank or RFQ prefill) default the footer contact to whoever is creating it
    if (!shaped.id) {
      shaped.contactPersonName = shaped.contactPersonName || admin?.name || '';
      shaped.contactPersonEmail = shaped.contactPersonEmail || admin?.email || '';
      shaped.contactPersonPhone = shaped.contactPersonPhone || admin?.phone || '';
    }
    setForm(shaped);
    setSavedSnapshot(shaped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuote, admin]);

  const subtotal = useMemo(() => form.lineItems.reduce((sum, item) => sum + lineAmount(item), 0), [form.lineItems]);
  const total = subtotal + (Number(form.salesTax) || 0) + (Number(form.otherAmount) || 0);
  const isDirty = JSON.stringify(form) !== JSON.stringify(savedSnapshot);

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const updateLineItem = (index, field, value) => {
    setForm((prev) => {
      const lineItems = prev.lineItems.map((item, i) => (i === index ? { ...item, [field]: value } : item));
      return { ...prev, lineItems };
    });
  };

  const addLineItem = () => setForm((prev) => ({ ...prev, lineItems: [...prev.lineItems, { ...EMPTY_LINE_ITEM }] }));

  const removeLineItem = (index) => setForm((prev) => ({
    ...prev,
    lineItems: prev.lineItems.length > 1 ? prev.lineItems.filter((_, i) => i !== index) : prev.lineItems,
  }));

  const buildPayload = () => ({
    ...form,
    lineItems: form.lineItems.map((item) => ({ ...item, amount: lineAmount(item) })),
    salesTax: Number(form.salesTax) || 0,
    otherAmount: Number(form.otherAmount) || 0,
  });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      const result = form.id ? await updateQuote(form.id, payload) : await createQuote(payload);
      const saved = toFormShape(result.data);
      setForm(saved);
      setSavedSnapshot(saved);
      setSuccess('Quote saved');
      onSaved?.(result.data);
    } catch (err) {
      setError(err.message || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format) => {
    setExportMenuAnchor(null);
    if (!form.id) return;
    setExporting(true);
    setError('');
    try {
      const { blob, filename } = await exportQuote(form.id, format);
      saveAs(blob, filename);
    } catch (err) {
      setError(err.message || 'Failed to export quote');
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setForm(savedSnapshot);
    setResetConfirm(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: SECONDARY }}>
          {form.quoteNumber ? `Quote ${form.quoteNumber}` : 'New Quote'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RestartAlt />}
            disabled={!isDirty}
            onClick={() => setResetConfirm(true)}
            sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY, borderColor: 'rgba(0,0,0,0.2)' }}
          >
            Reset
          </Button>
          <Tooltip title={!form.id ? 'Save the quote before exporting' : ''}>
            <span>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                endIcon={<ArrowDropDown />}
                disabled={!form.id || exporting}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, color: PRIMARY, borderColor: PRIMARY }}
              >
                Export
              </Button>
            </span>
          </Tooltip>
          <Menu anchorEl={exportMenuAnchor} open={!!exportMenuAnchor} onClose={() => setExportMenuAnchor(null)}>
            <MenuItem onClick={() => handleExport('pdf')}>PDF</MenuItem>
            <MenuItem onClick={() => handleExport('docx')}>DOCX</MenuItem>
            <MenuItem onClick={() => handleExport('html')}>HTML</MenuItem>
          </Menu>
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={saving}
            onClick={handleSave}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: PRIMARY,
              '&:hover': { backgroundColor: '#5a8499' },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Quote #" value={form.quoteNumber} onChange={setField('quoteNumber')} placeholder="Auto-generated on save" size="small" sx={inputSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <DatePicker
              label="Quote Date"
              value={form.quoteDate ? dayjs(form.quoteDate) : null}
              onChange={(newValue) => setForm((prev) => ({ ...prev, quoteDate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '' }))}
              slotProps={{ textField: { fullWidth: true, size: 'small', sx: inputSx } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth select label="Status" value={form.status} onChange={setField('status')} size="small" sx={inputSx}>
              {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW, height: '100%' }}>
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: PRIMARY, fontSize: '0.75rem', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1.5 }}>
              Quotation For
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={8}><TextField fullWidth label="Name" value={form.customerName} onChange={setField('customerName')} size="small" sx={inputSx} required /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Customer ID" value={form.customerId} onChange={setField('customerId')} size="small" sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Title" value={form.customerTitle} onChange={setField('customerTitle')} size="small" sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Company" value={form.customerCompany} onChange={setField('customerCompany')} size="small" sx={inputSx} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Address" value={form.customerAddress} onChange={setField('customerAddress')} size="small" sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={form.customerEmail} onChange={setField('customerEmail')} size="small" sx={inputSx} required /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={form.customerPhone} onChange={setField('customerPhone')} size="small" sx={inputSx} /></Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW, height: '100%' }}>
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: PRIMARY, fontSize: '0.75rem', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1.5 }}>
              Ship To
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}><TextField fullWidth label="Name" value={form.shipToName} onChange={setField('shipToName')} size="small" sx={inputSx} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Address" value={form.shipToAddress} onChange={setField('shipToAddress')} size="small" sx={inputSx} multiline minRows={2} /></Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={4} md={2}><TextField fullWidth label="Sales Person" value={form.salesPerson} onChange={setField('salesPerson')} size="small" sx={inputSx} /></Grid>
          <Grid item xs={6} sm={4} md={2}><TextField fullWidth label="P.O. Number" value={form.poNumber} onChange={setField('poNumber')} size="small" sx={inputSx} /></Grid>
          <Grid item xs={6} sm={4} md={2}>
            <DatePicker
              label="Ship Date"
              value={form.shipDate ? dayjs(form.shipDate) : null}
              onChange={(newValue) => setForm((prev) => ({ ...prev, shipDate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '' }))}
              slotProps={{ textField: { fullWidth: true, size: 'small', sx: inputSx } }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}><TextField fullWidth label="Ship Via" value={form.shipVia} onChange={setField('shipVia')} size="small" sx={inputSx} /></Grid>
          <Grid item xs={6} sm={4} md={2}><TextField fullWidth label="F.O.B. Point" value={form.fobPoint} onChange={setField('fobPoint')} size="small" sx={inputSx} /></Grid>
          <Grid item xs={6} sm={4} md={2}><TextField fullWidth label="Terms" value={form.terms} onChange={setField('terms')} size="small" sx={inputSx} multiline /></Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: SECONDARY }}>Line Items</Typography>
          <Button size="small" startIcon={<Add />} onClick={addLineItem} sx={{ textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, color: PRIMARY }}>
            Add Row
          </Button>
        </Box>
        {form.lineItems.map((item, index) => (
          <Grid container spacing={1} key={index} sx={{ mb: 1, alignItems: 'center' }}>
            <Grid item xs={6} sm={1}>
              <TextField fullWidth type="number" label="Qty" value={item.qty} onChange={(e) => updateLineItem(index, 'qty', e.target.value)} size="small" sx={inputSx} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth label="Product" value={item.product} onChange={(e) => updateLineItem(index, 'product', e.target.value)} size="small" sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Description" value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} size="small" sx={inputSx} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth type="number" label="Unit Price" value={item.unitPrice} onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)} size="small" sx={inputSx} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Tooltip title="Negative = discount, positive = additional charge">
                <TextField fullWidth type="number" label="Discount/Charge" value={item.discount} onChange={(e) => updateLineItem(index, 'discount', e.target.value)} size="small" sx={inputSx} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
              </Tooltip>
            </Grid>
            <Grid item xs={10} sm={1.5}>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY }}>
                ${lineAmount(item).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={2} sm={0.5}>
              <IconButton size="small" onClick={() => removeLineItem(index)} disabled={form.lineItems.length === 1} sx={{ color: '#d32f2f' }}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: SECONDARY }}>Footer Notes</Typography>
              <Tooltip title={footerEditing ? 'Done editing' : 'Edit footer notes'}>
                <IconButton size="small" onClick={() => setFooterEditing((prev) => !prev)} sx={{ color: PRIMARY }}>
                  {footerEditing ? <Check fontSize="small" /> : <Edit fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            {footerEditing ? (
              <Grid container spacing={1.5}>
                <Grid item xs={12}><TextField fullWidth label="Lead Time" value={form.leadTime} onChange={setField('leadTime')} size="small" sx={inputSx} placeholder="e.g. ~8–12 weeks" /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth label="Contact Name" value={form.contactPersonName} onChange={setField('contactPersonName')} size="small" sx={inputSx} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth label="Contact Phone" value={form.contactPersonPhone} onChange={setField('contactPersonPhone')} size="small" sx={inputSx} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth label="Contact Email" value={form.contactPersonEmail} onChange={setField('contactPersonEmail')} size="small" sx={inputSx} /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Payment Terms" value={form.paymentTerms} onChange={setField('paymentTerms')} size="small" sx={inputSx} /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Late Payment Terms" value={form.lateFeeTerms} onChange={setField('lateFeeTerms')} size="small" sx={inputSx} /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Confidentiality Notice" value={form.confidentialityNotice} onChange={setField('confidentialityNotice')} size="small" sx={inputSx} /></Grid>
              </Grid>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography sx={footerLabelSx}>Lead Time</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', color: form.leadTime ? SECONDARY : '#999' }}>
                    {form.leadTime || 'Not set'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={footerLabelSx}>Contact</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', color: SECONDARY }}>
                    {[form.contactPersonName, form.contactPersonPhone, form.contactPersonEmail].filter(Boolean).join(' · ') || 'Not set'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={footerLabelSx}>Payment Terms</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.82rem', color: '#555', lineHeight: 1.5 }}>{form.paymentTerms}</Typography>
                </Box>
                <Box>
                  <Typography sx={footerLabelSx}>Late Payment Terms</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.82rem', color: '#555', lineHeight: 1.5 }}>{form.lateFeeTerms}</Typography>
                </Box>
                <Box>
                  <Typography sx={footerLabelSx}>Confidentiality Notice</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.78rem', color: '#888', fontStyle: 'italic', lineHeight: 1.5 }}>{form.confidentialityNotice}</Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW, height: '100%' }}>
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: SECONDARY, mb: 1.5 }}>Totals</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontFamily: FONT_BODY, color: '#666' }}>Subtotal</Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>${subtotal.toFixed(2)}</Typography>
            </Box>
            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              <Grid item xs={6}><TextField fullWidth type="number" label="Sales Tax" value={form.salesTax} onChange={setField('salesTax')} size="small" sx={inputSx} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
              <Grid item xs={6}><TextField fullWidth type="number" label="Other" value={form.otherAmount} onChange={setField('otherAmount')} size="small" sx={inputSx} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
            </Grid>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: SECONDARY, borderRadius: '10px', px: 2, py: 1.5 }}>
              <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: '#fff' }}>TOTAL</Typography>
              <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: '#fff' }}>${total.toFixed(2)}</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button onClick={onBack} sx={{ textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 500, color: '#666' }}>
          Back
        </Button>
      </Box>

      <Dialog open={resetConfirm} onClose={() => setResetConfirm(false)} PaperProps={{ sx: { borderRadius: '1rem' } }}>
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>Reset changes?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY }}>
            This will discard unsaved changes and revert to the last saved version of this quote.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResetConfirm(false)} sx={{ textTransform: 'none', fontFamily: FONT_BODY, color: '#666' }}>Cancel</Button>
          <Button onClick={handleReset} variant="contained" sx={{ textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={2500} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" sx={{ fontFamily: FONT_BODY, borderRadius: RADIUS }} onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
    </LocalizationProvider>
  );
}
